import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './SalesReportForm.css';

function SalesReportForm({ report, onSave, onCancel }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    reportDate: new Date().toISOString().split('T')[0],
    totalOutboundCalls: 0,
    successfulConnections: 0,
    callDuration: 0,
    followUpInteractions: 0,
    followUpCompletions: 0,
    qualifiedProspects: 0,
    interestedProspects: 0,
    pipelineOpportunities: 0,
    meetingsScheduled: 0,
    dealsClosedValue: 0,
    dealsInProgress: 0,
    achievements: '',
    challenges: '',
    nextDayPlanning: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (report) {
      setFormData({
        reportDate: report.reportDate ? report.reportDate.split('T')[0] : new Date().toISOString().split('T')[0],
        totalOutboundCalls: report.totalOutboundCalls || 0,
        successfulConnections: report.successfulConnections || 0,
        callDuration: report.callDuration || 0,
        followUpInteractions: report.followUpInteractions || 0,
        followUpCompletions: report.followUpCompletions || 0,
        qualifiedProspects: report.qualifiedProspects || 0,
        interestedProspects: report.interestedProspects || 0,
        pipelineOpportunities: report.pipelineOpportunities || 0,
        meetingsScheduled: report.meetingsScheduled || 0,
        dealsClosedValue: report.dealsClosedValue || 0,
        dealsInProgress: report.dealsInProgress || 0,
        achievements: report.achievements || '',
        challenges: report.challenges || '',
        nextDayPlanning: report.nextDayPlanning || ''
      });
    }
  }, [report]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: e.target.type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      if (report?._id) {
        response = await api.put(`/sales-reports/${report._id}`, formData);
      } else {
        response = await api.post('/sales-reports', formData);
      }
      
      onSave && onSave(response.data);
    } catch (error) {
      console.error('Error saving sales report:', error);
      alert(error.response?.data?.message || 'Error saving sales report');
    } finally {
      setLoading(false);
    }
  };

  // Calculate derived metrics
  const connectionRate = formData.totalOutboundCalls > 0 ? 
    ((formData.successfulConnections / formData.totalOutboundCalls) * 100).toFixed(1) : 0;
  const conversionRate = formData.successfulConnections > 0 ? 
    ((formData.qualifiedProspects / formData.successfulConnections) * 100).toFixed(1) : 0;

  return (
    <div className="sales-report-form">
      <div className="form-header">
        <h3>📊 {report ? 'Update' : 'Create'} Daily Sales Report</h3>
        <p>Employee: {user?.firstName} {user?.lastName}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-sections">
          {/* Basic Info */}
          <div className="form-section">
            <div className="section-header" style={{display: 'block', visibility: 'visible', opacity: 1, fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '-30px -30px 25px -30px', padding: '20px 30px', background: 'linear-gradient(135deg, #e8edff 0%, #f5f7ff 100%)', borderBottom: '3px solid #667eea', borderRadius: '12px 12px 0 0'}}>📅 Report Details</div>
            <div className="form-row">
              <div className="form-group">
                <label>Report Date*</label>
                <input
                  type="date"
                  name="reportDate"
                  value={formData.reportDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* Call Metrics */}
          <div className="form-section">
            <div className="section-header" style={{display: 'block', visibility: 'visible', opacity: 1, fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '-30px -30px 25px -30px', padding: '20px 30px', background: 'linear-gradient(135deg, #e8edff 0%, #f5f7ff 100%)', borderBottom: '3px solid #667eea', borderRadius: '12px 12px 0 0'}}>📞 Call Activities</div>
            <div className="form-row">
              <div className="form-group">
                <label>Total Outbound Calls*</label>
                <input
                  type="number"
                  name="totalOutboundCalls"
                  value={formData.totalOutboundCalls}
                  onChange={handleChange}
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label>Successful Connections*</label>
                <input
                  type="number"
                  name="successfulConnections"
                  value={formData.successfulConnections}
                  onChange={handleChange}
                  min="0"
                  max={formData.totalOutboundCalls}
                  required
                />
              </div>
              <div className="form-group">
                <label>Total Call Duration (minutes)</label>
                <input
                  type="number"
                  name="callDuration"
                  value={formData.callDuration}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>
            <div className="metric-display">
              <span className="metric">Connection Rate: {connectionRate}%</span>
            </div>
          </div>

          {/* Follow-up Metrics */}
          <div className="form-section">
            <div style={{display: 'block', visibility: 'visible', opacity: 1, fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '-30px -30px 25px -30px', padding: '20px 30px', background: 'linear-gradient(135deg, #e8edff 0%, #f5f7ff 100%)', borderBottom: '3px solid #667eea', borderRadius: '12px 12px 0 0'}}>🔄 Follow-up Activities</div>
            <div className="form-row">
              <div className="form-group">
                <label>Follow-up Interactions</label>
                <input
                  type="number"
                  name="followUpInteractions"
                  value={formData.followUpInteractions}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Follow-up Completions</label>
                <input
                  type="number"
                  name="followUpCompletions"
                  value={formData.followUpCompletions}
                  onChange={handleChange}
                  min="0"
                  max={formData.followUpInteractions}
                />
              </div>
            </div>
          </div>

          {/* Lead Quality */}
          <div className="form-section">
            <div style={{display: 'block', visibility: 'visible', opacity: 1, fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '-30px -30px 25px -30px', padding: '20px 30px', background: 'linear-gradient(135deg, #e8edff 0%, #f5f7ff 100%)', borderBottom: '3px solid #667eea', borderRadius: '12px 12px 0 0'}}>🎯 Lead Quality</div>
            <div className="form-row">
              <div className="form-group">
                <label>Qualified Prospects*</label>
                <input
                  type="number"
                  name="qualifiedProspects"
                  value={formData.qualifiedProspects}
                  onChange={handleChange}
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label>Interested Prospects</label>
                <input
                  type="number"
                  name="interestedProspects"
                  value={formData.interestedProspects}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>
            <div className="metric-display">
              <span className="metric">Conversion Rate: {conversionRate}%</span>
            </div>
          </div>

          {/* Pipeline & Opportunities */}
          <div className="form-section">
            <div style={{display: 'block', visibility: 'visible', opacity: 1, fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '-30px -30px 25px -30px', padding: '20px 30px', background: 'linear-gradient(135deg, #e8edff 0%, #f5f7ff 100%)', borderBottom: '3px solid #667eea', borderRadius: '12px 12px 0 0'}}>💼 Sales Pipeline</div>
            <div className="form-row">
              <div className="form-group">
                <label>Pipeline Opportunities*</label>
                <input
                  type="number"
                  name="pipelineOpportunities"
                  value={formData.pipelineOpportunities}
                  onChange={handleChange}
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label>Meetings Scheduled</label>
                <input
                  type="number"
                  name="meetingsScheduled"
                  value={formData.meetingsScheduled}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Deals & Revenue */}
          <div className="form-section">
            <div style={{display: 'block', visibility: 'visible', opacity: 1, fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '-30px -30px 25px -30px', padding: '20px 30px', background: 'linear-gradient(135deg, #e8edff 0%, #f5f7ff 100%)', borderBottom: '3px solid #667eea', borderRadius: '12px 12px 0 0'}}>💰 Deals & Revenue</div>
            <div className="form-row">
              <div className="form-group">
                <label>Deals Closed Value (₹)</label>
                <input
                  type="number"
                  name="dealsClosedValue"
                  value={formData.dealsClosedValue}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Deals in Progress</label>
                <input
                  type="number"
                  name="dealsInProgress"
                  value={formData.dealsInProgress}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Notes & Planning */}
          <div className="form-section">
            <div style={{display: 'block', visibility: 'visible', opacity: 1, fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '-30px -30px 25px -30px', padding: '20px 30px', background: 'linear-gradient(135deg, #e8edff 0%, #f5f7ff 100%)', borderBottom: '3px solid #667eea', borderRadius: '12px 12px 0 0'}}>📝 Notes & Planning</div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Today's Achievements</label>
                <textarea
                  name="achievements"
                  value={formData.achievements}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Describe your key achievements today..."
                  maxLength="500"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Challenges Faced</label>
                <textarea
                  name="challenges"
                  value={formData.challenges}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Describe any challenges or obstacles..."
                  maxLength="500"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Tomorrow's Planning</label>
                <textarea
                  name="nextDayPlanning"
                  value={formData.nextDayPlanning}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Outline your plans for tomorrow..."
                  maxLength="500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-submit"
            disabled={loading}
          >
            {loading ? 'Saving...' : (report ? 'Update Report' : 'Submit Report')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SalesReportForm;